import * as React from 'react';
import type { ApiErrorResponse, ProjectResponse, ShowProjectsProps } from '@/types';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { DATE_FORMAT, FALLBACK_IMG } from '@/constants';
import { ProjectCuHandler } from './project-cu-handler';
import { ConfirmDialog, type ConfirmDialogConfig } from '../confrim-dialog';
import { useProjectsMenuService } from '@/hooks/use-projects-menu-service';
import { statusTone, getStatusLabel } from '@/lib';
import { ProjectMenu } from './project-menus';
import { AvatarEditor } from '../avatar-editor';
import WithLoadingErrorWrapper from '../with-loading-error-wrapper';
import AssignMentor from './assign-mentory';
import { buildProjectMenuItems } from './project-menu-config';
import NoData from '../no-data';
import { useClientPermissions } from '@/hooks/use-client-permissions';
import ShowBoards from './show-boards';
import { Button } from '../ui/button';
import { Play, Loader2 } from 'lucide-react';
import { useStartEvaluationMutation, useStartExternalEvaluationMutation } from '@/state';
import { useTenant } from '@/hooks';
import { toast } from 'sonner';
import { EvaluationModal } from './internal/show-project-details/evaluation-modal';

function MetricPill({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border bg-muted/40 px-3 py-1.5 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-foreground">{value}</span>
    </div>
  );
}

export const ShowProjects: React.FC<ShowProjectsProps> = ({
  projects,
  projectType,
  assignedInternalProjects,
}) => {
  const { isCompany } = useClientPermissions();
  const {
    // state
    openMenuId,
    setOpenMenuId,
    showUpdateProject,
    setShowUpdateProject,
    selectedProject,
    openFor,
    setOpenFor,
    showDialog,
    setShowDialog,
    image,
    confirmOpen,
    setConfirmOpen,
    confirmConfig,
    setConfirmConfig,
    mentorSheetProject,
    setMentorSheetProject,
    currentUser,

    // loading / error
    deletingLoading,
    thumbnailLoading,
    duplicateLoading,
    assignLoading,
    updateThumbnailExternalLoading,
    thumbnailError,

    // handlers
    handleEdit,
    handleThumbnailChange,
    handleDelete,
    updateThumbnailBanner,
    handleDuplicate,
    handleAssignMentor,
    handleProjectClick,
  } = useProjectsMenuService({ projectType, assignedInternalProjects });

  const { isStudent } = useClientPermissions();
  const { tenant } = useTenant();
  const [startEvaluation, { isLoading: isStartingEvaluation }] = useStartEvaluationMutation();
  const [startExternalEvaluation, { isLoading: isStartingExternalEvaluation }] = useStartExternalEvaluationMutation();
  const [showEvaluation, setShowEvaluation] = React.useState(false);
  const [evalProject, setEvalProject] = React.useState<ProjectResponse | null>(null);
  const [evaluatingProjectId, setEvaluatingProjectId] = React.useState<number | null>(null);

  const emptyState = React.useMemo(() => {
    const isInternal = projectType === 'INTERNAL';
    const isExternal = projectType === 'EXTERNAL';
    const isAssigned = !!assignedInternalProjects;

    if (isInternal && isAssigned) {
      return {
        title: 'No Projects Assigned',
        subTitle: 'You have not been assigned to any internal projects yet.',
      };
    }
    if (isInternal) {
      return {
        title: 'No Internal Projects',
        subTitle: 'There are no internal projects created for this department yet.',
      };
    }
    if (isExternal && isAssigned) {
      return {
        title: 'No External Projects',
        subTitle: 'You have not been assigned to any external projects yet.',
      };
    }
    return {
      title: 'No Projects Found',
      subTitle: 'Start by creating your first project using the wizard above.',
    };
  }, [projectType, assignedInternalProjects]);

  if (!projects || projects.length === 0) {
    return (
      <NoData title={emptyState.title} subTitle={emptyState.subTitle} />
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
      {showDialog && (
        <AvatarEditor
          open={showDialog}
          onClose={() => setShowDialog(false)}
          onSave={updateThumbnailBanner}
          image={image}
          borderRadius={0}
          aspectRatio={1.5}
        />
      )}

      {confirmOpen && (
        <ConfirmDialog
          open={confirmOpen}
          setOpen={setConfirmOpen}
          config={confirmConfig as ConfirmDialogConfig | null}
          loading={deletingLoading}
        />
      )}

      {showUpdateProject && (
        <ProjectCuHandler
          openFor={openFor}
          title="Update Project"
          projectType={projectType}
          open={showUpdateProject}
          setOpen={setShowUpdateProject}
          setOpenFor={setOpenFor}
          project={selectedProject ?? undefined}
        />
      )}

      <AssignMentor
        open={!!mentorSheetProject}
        projectName={mentorSheetProject?.name}
        currentUserId={currentUser?.id}
        clientsLoading={false}
        onClose={() => setMentorSheetProject(null)}
        onSelect={handleAssignMentor}
      />

      <ShowBoards show={null} onClose={() => { }} />

      {projects?.map((project) => {
        const isHeaderLoading =
          (thumbnailLoading ||
            updateThumbnailExternalLoading ||
            assignLoading ||
            deletingLoading ||
            duplicateLoading) &&
          selectedProject?.id === project.id;

        return (
          <WithLoadingErrorWrapper
            key={project.id}
            loading={isHeaderLoading}
            error={thumbnailError as ApiErrorResponse}
          >
            <Card
              className="group relative h-full cursor-pointer overflow-hidden border-0 bg-card/40 transition-all duration-300 hover:bg-card/60 hover:shadow-2xl hover:shadow-black/5"
              onClick={() => {
                handleProjectClick(
                  project.id,
                  project.project_resorces_id,
                  project.project_id
                );
              }}
            >
              <div
                className="relative h-[180px] w-full overflow-hidden"
              >
                <img
                  src={project.project_banner || FALLBACK_IMG}
                  alt={project.name}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = FALLBACK_IMG;
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                <div className="absolute right-3 top-3 z-10 opacity-0 transition-all duration-300 group-hover:opacity-100">
                  {!isStudent && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <ProjectMenu
                        open={openMenuId === project.id}
                        onOpenChange={(next) =>
                          setOpenMenuId(next ? project.id : null)
                        }
                        project={project}
                        items={buildProjectMenuItems(projectType, {
                          onEdit: handleEdit,
                          onChangeThumbnail: handleThumbnailChange,
                          onDuplicate: handleDuplicate,
                          onAssignMentor: (p) => setMentorSheetProject(p),
                          onDelete: handleDelete,
                          onMoveToBoard: () => { },
                        }, isCompany)}
                      />
                    </div>
                  )}
                </div>

                <div className="absolute left-3 top-3">
                  <Badge
                    variant="outline"
                    className={[
                      'rounded-full px-3 py-1 text-xs',
                      'bg-background/70 backdrop-blur-md',
                      statusTone(project?.project_status as string),
                    ].join(' ')}
                  >
                    {project.publiclyAssigned
                      ? 'Assigned'
                      : getStatusLabel(project.project_status as string)}
                  </Badge>
                </div>
              </div>

              <div className="flex h-[190px] flex-col justify-between p-4">
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-base font-semibold leading-snug line-clamp-2">
                      {project.name}
                    </h3>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary" className="rounded-full">
                      {project.project_duration} days
                    </Badge>
                    <Badge variant="secondary" className="rounded-full">
                      Mentor: {project.project_mentor ?? 'Not assigned'}
                    </Badge>
                  </div>
                </div>

                <div className="mt-4 flex flex-col gap-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap gap-2">
                      <MetricPill
                        label="Enrollments"
                        value={project.number_of_enrollments}
                      />
                      <MetricPill label="Docs" value={project.number_of_docs} />
                    </div>

                    {/* Start Evaluation Button for Mentors */}
                    {!isCompany && projectType === 'EXTERNAL' && project.project_mentor && currentUser?.email?.toLowerCase() === project.project_mentor.toLowerCase() && (Number(project.project_status) === 2 || Number(project.project_status) === 4) && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 gap-1.5 rounded-full border-primary/20 bg-primary/5 text-primary hover:bg-primary/10"
                        onClick={async (e) => {
                          e.stopPropagation();

                          const hasMembers = (project.members?.length ?? 0) > 0;
                          const hasSkills = (project.expected_skills?.length ?? 0) > 0;

                          if (!hasMembers || !hasSkills) {
                            setConfirmConfig({
                              title: 'Evaluation Requirements Not Met',
                              description: `To start an evaluation cycle, the project must have:
${!hasMembers ? '\n• At least one project member/learner' : ''}
${!hasSkills ? '\n• At least one skill defined' : ''}

Please add the missing information before proceeding.`,
                              confirmText: 'Understood',
                              cancelText: 'Close',
                              onConfirm: () => setConfirmOpen(false),
                            });
                            setConfirmOpen(true);
                            return;
                          }

                          try {
                            setEvaluatingProjectId(project.id);
                            if (projectType === 'EXTERNAL') {
                              await startExternalEvaluation({
                                projectId: project.id,
                              }).unwrap();
                            } else {
                              await startEvaluation({
                                tenant: tenant!,
                                projectId: project.project_resorces_id || project.id,
                              }).unwrap();
                            }
                            setEvalProject(project);
                            setShowEvaluation(true);
                            toast.success('Evaluation cycle started');
                          } catch (error) {
                            toast.error('Failed to start evaluation');
                          } finally {
                            setEvaluatingProjectId(null);
                          }
                        }}
                        disabled={(isStartingEvaluation || isStartingExternalEvaluation) && evaluatingProjectId === project.id}
                      >
                        {(isStartingEvaluation || isStartingExternalEvaluation) && evaluatingProjectId === project.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Play className="h-3.5 w-3.5 fill-current" />
                        )}
                        Start Evaluation
                      </Button>
                    )}
                  </div>

                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                      Created{' '}
                      {format(new Date(project.created_on), DATE_FORMAT)}
                    </span>
                    <span className="truncate">ID: {project.id}</span>
                  </div>
                </div>
              </div>
            </Card>
          </WithLoadingErrorWrapper>
        );
      })}

      <EvaluationModal
        open={showEvaluation}
        onOpenChange={setShowEvaluation}
        projectId={evalProject?.id || 0}
        members={evalProject?.members || []}
        skills={evalProject?.expected_skills || []}
        isExternal={projectType === 'EXTERNAL'}
      />
    </div>
  );
};
